import { mapState } from 'vuex'

export default {
  methods: {
    $checkUserIsAdmin (user) {
      return user.role === 'admin' || user.role === 'regional-admin' || user.role === 'super-regional-admin' || user.role === 'restricted-admin'
    },

    $userIsBookingRegionalAdmin (user, booking) {
      return (user.role === 'admin' || user.role === 'restricted-admin') || (this.$checkUserIsAdmin(user) && user.region_id === booking.region_id)
    },

    $stripePublicKey (regionShortname) {
      switch (regionShortname) {
        case this.$regionNameAu:
          return process.env.AU_STRIPE_API_KEY
        case this.$regionNameUk:
          return process.env.GB_STRIPE_API_KEY
        case this.$regionNameUs:
          return process.env.US_STRIPE_API_KEY
        default:
          return null
      }
    },

    $hasErrorClass (field, classes) {
      return field && field.$error ? `${classes} has-error` : `${classes}`
    }
  },

  computed: {
    ...mapState('clientConfig', ['client_config']),

    clientConfig () {
      return this.$clientConfig && this.$clientConfig.modules ? this.$clientConfig : this.client_config
    },

    /**
     * Determine if user is logged in.
     *
     * @return bool
     */
    $userIsLoggedIn () {
      return this.$user !== null && this.$user.id !== undefined
    },

    $userIsReportingAdmin () {
      return this.$userIsLoggedIn && this.$user.role === 'reporting-admin'
    },

    /**
     * Determine if user is not logged in.
     *
     * @return boolean
     */
    $userIsNotLoggedIn () {
      return !this.$userIsLoggedIn
    },

    /**
     * Determine if user is an admin.
     *
     * @return boolean
     */
    $userIsAdmin () {
      return this.$userIsLoggedIn && (this.$user.role === 'admin' || this.$user.role === 'regional-admin' || this.$user.role === 'super-regional-admin' || this.$user.role === 'restricted-admin')
    },

    /**
     * Determine if user is a global admin.
     * TODO: we could look at re-factoring this into "anyAdmin", "admin", "regionalAdmin" etc.
     *
     * @return boolean
     */
    $userIsGlobalAdmin () {
      return this.$userIsLoggedIn && (this.$user.role === 'admin' || this.$user.role === 'restricted-admin')
    },

    $userIsSuperAdmin () {
      return this.$userIsLoggedIn && this.$user.role === 'admin'
    },

    $userIsNonGlobalAdmin () {
      return this.$userIsRegionalAdmin || this.$userIsSuperRegionalAdmin
    },

    /**
     * Determine if user is a restricted admin.
     *
     * @return boolean
     */
    $userIsRestrictedAdmin () {
      return this.$userIsLoggedIn && this.$user.role === 'restricted-admin'
    },

    /**
     * Determine if user is a regional admin.
     *
     * @return boolean
     */
    $userIsRegionalAdmin () {
      return this.$userIsLoggedIn && this.$user.role === 'regional-admin'
    },

    /**
     * Determine if user is a regional super admin.
     *
     * @return boolean
     */
    $userIsSuperRegionalAdmin () {
      return this.$userIsLoggedIn && this.$user.role === 'super-regional-admin'
    },

    /**
     * Determine if user is an intern.
     *
     * @return boolean
     */
    $userIsIntern () {
      return this.$userIsLoggedIn && this.$user.role === 'intern'
    },

    /**
     * Determine is user is an auditor.
     *
     * An auditor is a Pickstar team member without full admin privileges
     * In general, they can view things, but not make changes
     *
     * @return boolean
     */
    $userIsAuditor () {
      return this.$userIsLoggedIn() && this.$user.role === 'auditor'
    },

    /**
     * Determine if user is a manager.
     *
     * @return boolean
     */
    $userIsManager () {
      return this.$userIsLoggedIn && this.$user.role === 'manager'
    },

    /**
     * Determine if user is a master manager.
     *
     * @return boolean
     */
    $userIsMasterManager () {
      return this.$userIsManager &&
        (this.$user.parent_id === null || this.$user.parent_id === undefined)
    },

    /**
     * Determine if user is a staff manager.
     *
     * @return boolean
     */
    $userIsStaffManager () {
      return this.$userIsManager &&
        this.$user.parent_id !== null
    },

    /**
     * Determine if user is a talent.
     *
     * @return boolean
     */
    $userIsTalent () {
      return this.$userIsLoggedIn && this.$user.role === 'talent'
    },

    /**
     * Determine if user is activated.
     *
     * @return boolean
     */
    $userIsActivated () {
      return this.$userIsLoggedIn && this.$user.status === 'active'
    },

    $userIsInactiveManager () {
      return this.$userIsManager && !this.$userIsActivated
    },

    /**
     * Determine if user is pending.
     *
     * @return boolean
     */
    $userIsPending () {
      return this.$userIsLoggedIn && this.$user.status === 'pending'
    },

    /**
     * Determine if user is not a talent.
     *
     * @return boolean
     */
    $userIsNotTalent () {
      return this.$userIsLoggedIn && this.$userIsTalent === false
    },

    /**
     * Determine if user is not a client.
     *
     * @return boolean
     */
    $userIsNotClient () {
      return this.$userIsLoggedIn && this.$userIsClient === false
    },

    /**
     * Determine if user is a client.
     *
     * @return boolean
     */
    $userIsClient () {
      return this.$userIsLoggedIn && this.$user.role === 'client'
    },

    /**
     * Determine if user is unverified.
     *
     * @return boolean
     */
    $userIsUnverified () {
      return this.$userIsLoggedIn && this.$user.email_verified === false
    },

    $paymentsModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.payments_module : false
    },

    $invoicesModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.send_invoice_module : false
    },

    $invoicesEnabledForUser () {
      if (!this.$invoicesModuleEnabled) {
        return false
      }
      if (this.$userIsAdmin) {
        return true
      }
      if (this.$userIsClient && this.clientConfig ? this.clientConfig.modules.client_invoice : false) {
        return true
      }
      if ((this.$userIsTalent || this.$userIsManager) && this.clientConfig ? this.clientConfig.modules.talent_invoice : false) {
        return true
      }
      return false
    },

    $vlastMode () {
      return this.clientConfig ? this.clientConfig.modules.vlast_mode : false
    },

    $clientSetsBudget () {
      return this.clientConfig ? this.clientConfig.modules.client_sets_budget : false
    },

    $clientDepartment () {
      return this.clientConfig ? this.clientConfig.modules.client_department : false
    },

    $showClientPii () {
      return this.clientConfig ? this.clientConfig.modules.show_client_pii : true
    },

    $multipleLeagues () {
      return this.clientConfig ? this.clientConfig.modules.multiple_leagues : false
    },

    $whitelabel () {
      return this.clientConfig ? this.clientConfig.modules.whitelabel : null
    },

    clientName () {
      return this.clientConfig ? this.clientConfig.tenant_details.client_name : null
    },

    contactEmail () {
      return this.clientConfig ? this.clientConfig.tenant_details.contact_email : null
    },

    clientContactEmail () {
      return this.clientConfig ? this.clientConfig.tenant_details.client_contact_email : null
    },

    defaultCurrency () {
      return this.clientConfig ? this.clientConfig.tenant_details.default_currency : null
    },

    monetaryCurrency () {
      return this.clientConfig ? this.clientConfig.tenant_details.default_currency !== 'points' : null
    },

    brandPrimary () {
      return this.clientConfig ? this.clientConfig.colours.brand_primary : null
    },

    contactNumber () {
      return this.clientConfig ? this.clientConfig.tenant_details.client_contact_number : null
    },

    vlastContactNumber () {
      return this.clientConfig ? this.clientConfig.tenant_details.vlast_contact_number : null
    },

    superannuationContribution () {
      return this.clientConfig ? this.clientConfig.tenant_details.superannuation_contribution : null
    },

    mobileLink () {
      return this.clientConfig ? this.clientConfig.tenant_details.app_link : null
    },

    $activeClientModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.active_client_module : false
    },

    $billboardModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.billboard_module : false
    },

    $managerModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.manager_module : false
    },

    $talentDateOfBirthEnabled () {
      return this.clientConfig ? this.clientConfig.modules.talent_date_of_birth : false
    },

    $offersModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.offers_enabled : false
    },

    $phoneFieldName () {
      return this.$user.region.shortname === this.$regionNameUs ? 'Cell phone' : 'Mobile phone'
    },

    $regionNameAu () {
      return 'au'
    },

    $regionNameUk () {
      return 'gb'
    },

    $regionNameUs () {
      return 'us'
    },

    $privacyEmail () {
      return this.clientConfig ? this.clientConfig.tenant_details.privacy_email : null
    },

    $cumulativeSearchEnabled () {
      return this.clientConfig ? this.clientConfig.modules.cumulative_results : false
    },

    $showDepartmentField () {
      return this.clientConfig ? this.clientConfig.modules.show_department_field : false
    },

    $declineModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.decline_module : false
    },

    $opportunityPointsModuleSum () {
      return this.clientConfig ? this.clientConfig.modules.opportunity_points_module_sum : false
    },

    $opportunityPointsModuleEnabled () {
      if (!this.clientConfig) {
        return null
      }
      if (typeof this.clientConfig.modules.opportunity_points_module === 'boolean') {
        return this.clientConfig.modules.opportunity_points_module
      } else if (typeof this.clientConfig.modules.opportunity_points_module === 'number') {
        return !!this.clientConfig.modules.opportunity_points_module // we're changing this to true/false;
      } else {
        return !!parseInt(this.clientConfig.modules.opportunity_points_module) // we're changing this to true/false;
      }
    },

    $opportunityBudgetsModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.opportunity_budgets_module : false
    },

    $talentContractsModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.talent_contracts_module : false
    },

    $costReferences () {
      return this.clientConfig ? this.clientConfig.modules.cost_references : false
    },

    $isNrlClient () {
      return ['Gold Coast Titans', 'Wests Tigers', 'NRL', 'Demo', 'Brumbies Appearances'].includes(this.clientName)
    },

    $isNrlClub () {
      return ['Gold Coast Titans', 'Wests Tigers', 'Brumbies Appearances'].includes(this.clientName)
    },

    $isAflClub () {
      return this.clientConfig ? this.clientConfig.modules.afl_club : false
    },

    $isNflClub () {
      return this.clientConfig ? this.clientConfig.modules.nfl_club : false
    },

    $isNflClient () {
      if (this.clientConfig) {
        return ['nflplayerrequest'].includes(this.clientConfig.slug)
      }
      return false
    },

    $isCricketAus () {
      if (this.clientConfig) {
        return ['cricket-australia', 'cricketaus'].includes(this.clientConfig.slug)
      }
      return false
    },

    $isACA () {
      if (this.clientConfig) {
        return ['auscricket', 'aus-cricket'].includes(this.clientConfig.slug)
      }
      return false
    },

    $isWSL () {
      if (this.clientConfig) {
        return ['wsl', 'world-surf-league', 'wsldemo'].includes(this.clientConfig.slug)
      }
      return false
    },

    $isOtis () {
      if (this.clientConfig) {
        return ['otis', 'otis-entertainment', 'otisentertainment'].includes(this.clientConfig.slug)
      }
      return false
    },

    $isQas () {
      if (this.clientConfig) {
        return ['qas', 'queensland-academy-of-sport', 'queenslandacademyofsport'].includes(this.clientConfig.slug)
      }
      return false
    },

    $commentModule () {
      return this.clientConfig ? this.clientConfig.modules.comment_module : null
    },

    $hideTravelTime () {
      return this.clientConfig ? this.clientConfig.modules.hide_travel_time : false
    },

    $hideSocialMediaReferences () {
      return this.clientConfig ? this.clientConfig.modules.hide_social_media_references : false
    },

    $starLabel () {
      return this.clientConfig ? this.clientConfig.tenant_details.star_label : null
    },

    $starLabelPlural () {
      return this.clientConfig ? this.clientConfig.tenant_details.star_label_plural : null
    },

    $requestFormConfig () {
      return this.clientConfig ? this.clientConfig.request_form : false
    },

    $tenantName () {
      return window.location.hostname.split('.')[0]
    },

    $showCategoryStats () {
      return this.clientConfig ? this.clientConfig.modules.show_category_stats : false
    },

    $positionTags () {
      return this.clientConfig ? this.clientConfig.modules.position_tags : null
    },

    $highSchoolTags () {
      return this.$isNflClub
    },

    $collegeTags () {
      return this.$isNflClub
    },

    $degreeCompletedTags () {
      return this.$isNflClub
    },

    $budgetRangeTags () {
      return this.clientConfig ? this.clientConfig.modules.budget_range : null
    },

    $proBowlTags () {
      return this.$isNflClub
    },

    $firstContractTags () {
      return this.$isNflClub
    },

    $charityTags () {
      return this.$vlastMode
    },

    $brandTags () {
      return this.$vlastMode && !this.$isNflClub
    },

    $awardTags () {
      return this.clientConfig ? this.clientConfig.modules.award_tags : false
    },

    $superannuationNumber () {
      return this.clientConfig ? this.clientConfig.modules.superannuation_number : false
    },

    $budgetCode () {
      return this.clientConfig ? this.clientConfig.modules.budget_code : false
    },

    $showDressCode () {
      return this.clientConfig ? this.clientConfig.modules.show_dress_code : false
    },

    $showStateStats () {
      return this.clientConfig ? this.clientConfig.modules.show_state_stats : false
    },

    $showTax () {
      return this.clientConfig ? this.clientConfig.modules.show_tax : false
    },

    $retired () {
      return this.clientConfig ? this.clientConfig.modules.retired : null
    },

    $swiftCode () {
      return this.clientConfig ? this.clientConfig.modules.swift_code : false
    },

    $soleTrader () {
      return this.clientConfig ? this.clientConfig.modules.sole_trader : false
    },

    $statementBySupplier () {
      return this.clientConfig ? this.clientConfig.modules.statement_by_supplier : false
    },

    $campaigns () {
      return this.clientConfig ? this.clientConfig.modules.campaigns : false
    },

    $hidePublicPromotingStatus () {
      return this.clientConfig ? this.clientConfig.modules.hide_public_promoting_status : false
    },

    $declineCommentModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.decline_comment : false
    },

    $allowRequestFileUploads () {
      return this.clientConfig ? (this.clientConfig.modules.allow_request_file_uploads || this.clientConfig.modules.offers_enabled) : false
    },

    $genderModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.gender : false
    },

    $refineOpportunitiesWithCategories () {
      return this.clientConfig ? this.clientConfig.modules.refine_opportunities_with_categories : false
    },

    $categoryModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.category_module : false
    },

    $clientsCanMakeBookings () {
      return this.clientConfig ? this.clientConfig.modules.clients_can_make_bookings : false
    },

    $paymentDetails () {
      return this.clientConfig ? this.clientConfig.modules.payment_details : false
    },

    $hasTagNodes () {
      return this.clientConfig ? this.clientConfig.tenant_details.has_tag_nodes : false
    },

    $hasInterestCategories () {
      return this.clientConfig ? this.clientConfig.tenant_details.has_interest_categories : false
    },

    $hasPersonalInterests () {
      return this.clientConfig ? this.clientConfig.tenant_details.has_personal_interests : false
    },

    $chatsModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.chats_module : false
    },

    $showTalentStatsForClient () {
      return this.clientConfig ? this.clientConfig.modules.talent_stats_for_clients : false
    },

    $restrictedAdminPermission () {
      return this.clientConfig ? this.clientConfig.modules.restricted_admin_permissions : false
    },

    $allowBookingDateRange () {
      return this.clientConfig ? this.clientConfig.modules.allow_booking_date_range : false
    },

    $showClientDetailsBookingModuleEnabled () {
      return this.clientConfig ? this.clientConfig.modules.show_client_details_booking : false
    },

    $showClientBIBudgetPerStateEnabled () {
      return this.clientConfig ? this.clientConfig.modules.show_client_booking_budget_per_state : false
    }
  }
}
